<template>
  <div class="support-page" :class="{ 'support-page--dark': $vuetify.theme.dark }">
    <v-container fluid class="support-container">
      <header class="support-heading">
        <div>
          <h1>Support</h1>
          <p>We're here to help you. Create a new support ticket or view your previous conversations.</p>
        </div>

        <v-dialog v-model="dialog" max-width="560" content-class="support-dialog">
          <template #activator="{ on, attrs }">
            <v-btn class="new-support-btn" v-bind="attrs" v-on="on" depressed>
              <v-icon left size="20">mdi-plus</v-icon>New Support
            </v-btn>
          </template>
          <v-card class="support-modal" :class="{ 'support-modal--light': !$vuetify.theme.dark }">
            <v-card-title>Create New Support</v-card-title>
            <v-card-text>
              <v-select v-model="selectedOption" :items="options" label="Select topic" outlined hide-details class="support-form-field mb-4" />
              <v-text-field v-if="selectedOption === 'Others'" v-model="subject" label="Subject" outlined hide-details class="support-form-field mb-4" />
              <v-textarea v-model="description" label="Describe your issue" outlined hide-details rows="5" class="support-form-field" />
            </v-card-text>
            <v-card-actions class="px-6 pb-6">
              <v-spacer />
              <v-btn text @click="dialog = false">Cancel</v-btn>
              <v-btn class="new-support-btn" depressed @click="createTicket">Create Ticket</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </header>

      <section v-if="tickets.length" class="support-workspace">
        <aside class="ticket-panel">
          <div class="ticket-tools">
            <div class="ticket-search">
              <v-icon size="21">mdi-magnify</v-icon>
              <input v-model="search" type="search" placeholder="Search tickets..." />
            </div>
            <v-btn icon class="filter-btn" aria-label="Filter tickets"><v-icon size="20">mdi-filter-variant</v-icon></v-btn>
          </div>

          <div class="ticket-list">
            <button
              v-for="(ticket, index) in filteredTickets"
              :key="ticket.id || index"
              type="button"
              class="ticket-item"
              :class="{ 'ticket-item--active': ticketDetails && ticketDetails.id === ticket.id }"
              @click="selectTicket(ticket, index)"
            >
              <span class="ticket-icon" :class="ticketIconClass(ticket.subject)">
                <v-icon size="27">{{ ticketIcon(ticket.subject) }}</v-icon>
              </span>
              <span class="ticket-copy">
                <span class="ticket-title-row">
                  <strong>{{ ticket.subject }}</strong>
                  <span class="status-pill" :class="{ 'status-pill--closed': ticket.status == 2 }">{{ ticket.status == 2 ? 'Closed' : 'Open' }}</span>
                </span>
                <span class="ticket-preview">{{ ticket.description || 'No description' }}</span>
                <span class="ticket-meta">
                  <span>{{ moment(ticket.created_at).format('ddd MMM, YYYY') }}</span>
                  <span>#SUP-{{ ticket.id }}</span>
                </span>
              </span>
            </button>

            <div v-if="!filteredTickets.length" class="no-results">No tickets match your search.</div>
          </div>

          <div class="ticket-footer">Showing {{ filteredTickets.length }} of {{ tickets.length }} tickets</div>
        </aside>

        <main v-if="ticketDetails && ticketDetails.id" ref="conversationPanel" class="conversation-panel">
          <header class="conversation-header">
            <span class="ticket-icon" :class="ticketIconClass(ticketDetails.subject)">
              <v-icon size="28">{{ ticketIcon(ticketDetails.subject) }}</v-icon>
            </span>
            <div class="conversation-title">
              <div><h2>{{ ticketDetails.subject }}</h2><span class="status-pill" :class="{ 'status-pill--closed': ticketDetails.status == 2 }">{{ ticketDetails.status == 2 ? 'Closed' : 'Open' }}</span></div>
              <p>
                <span>Ticket ID: #SUP-{{ ticketDetails.id }}</span>
                <span><v-icon size="16">mdi-calendar-blank-outline</v-icon>{{ moment(ticketDetails.created_at).format('MMMM Do YYYY') }}</span>
                <span><v-icon size="16">mdi-tag-outline</v-icon>General Inquiry</span>
              </p>
            </div>
            <v-btn icon class="more-btn"><v-icon>mdi-dots-vertical</v-icon></v-btn>
          </header>

          <div ref="messageCard" class="messages">
            <div class="message-row message-row--user">
              <div class="message-bubble message-bubble--user">
                <div class="message-info"><span>{{ moment(ticketDetails.created_at).fromNow() }}</span><strong>You</strong></div>
                <p>{{ ticketDetails.description }}</p>
              </div>
              <span class="user-avatar"><v-icon color="white">mdi-account-outline</v-icon></span>
            </div>

            <div
              v-for="(item, index) in ticketDetails.ticketcomments"
              :key="index"
              class="message-row"
              :class="{ 'message-row--user': item.is_admin_comment === 0 }"
            >
              <span v-if="item.is_admin_comment === 1" class="staff-avatar">
                <img src="../../assets/avatar.png" alt="Support team" /><i></i>
              </span>
              <div class="message-bubble" :class="{ 'message-bubble--user': item.is_admin_comment === 0 }">
                <div class="message-info">
                  <template v-if="item.is_admin_comment === 1"><strong>Support Team</strong><em>Staff</em><span>{{ moment(item.created_at).fromNow() }}</span></template>
                  <template v-else><span>{{ moment(item.created_at).fromNow() }}</span><strong>You</strong></template>
                </div>
                <p>{{ item.comment }}</p>
                <v-img v-if="item.image" :src="item.image" max-width="220" class="mt-3 rounded-lg" />
              </div>
              <span v-if="item.is_admin_comment === 0" class="user-avatar"><v-icon color="white">mdi-account-outline</v-icon></span>
            </div>
            <div ref="scrollAnchor"></div>
          </div>

          <footer v-if="ticketDetails.status == 1" class="reply-box">
            <div class="reply-tabs"><button class="active">Reply</button><button>Note</button></div>
            <textarea v-model="comment" placeholder="Type your message here..." @keydown.ctrl.enter="submitReply(ticketDetails.id)"></textarea>
            <div v-if="selectedImage" class="image-preview">
              <v-img :src="imagePreview" max-width="120" max-height="80" contain /><v-btn icon small @click="clearImage"><v-icon size="18">mdi-close</v-icon></v-btn>
            </div>
            <div class="reply-actions">
              <div>
                <v-file-input ref="fileInput" accept="image/*" hide-input prepend-icon="mdi-paperclip" class="attachment-input" @change="handleImageUpload" />
                <v-icon size="23">mdi-emoticon-happy-outline</v-icon>
                <v-icon size="23" @click="$refs.fileInput.$refs.input.click()">mdi-image-outline</v-icon>
              </div>
              <v-btn class="send-btn" depressed :loading="isPendingReply" :disabled="!comment.trim() && !selectedImage" @click="submitReply(ticketDetails.id)">
                <v-icon left size="18">mdi-send-outline</v-icon>Send Reply
              </v-btn>
            </div>
          </footer>
          <div v-else class="closed-notice"><v-icon size="19">mdi-lock-outline</v-icon>This ticket is closed</div>
        </main>
      </section>

      <section v-else class="empty-support">
        <v-icon size="48">mdi-message-text-outline</v-icon>
        <h2>No support tickets yet</h2>
        <p>Create a new support request and our team will be happy to help.</p>
      </section>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapState } from "vuex";
import toast from "@/services/Notification";
import { TICKETS__ACTIONS } from "@/store/action-types";

export default {
  name: "SupportTicket",
  data: () => ({
    search: "", subject: "", description: "", comment: "", dialog: false,
    selectedOption: null, options: ["Account", "Deposit", "Withdraw", "Others"],
    selectedTab: 0, refreshInterval: null, lastMessageCount: 0,
    selectedImage: null, imagePreview: null, isPendingReply: false,
  }),
  computed: {
    ...mapState("tickets", ["tickets", "ticketDetails"]),
    filteredTickets() {
      const query = this.search.trim().toLowerCase();
      return query ? this.tickets.filter(ticket => (ticket.subject || "").toLowerCase().includes(query)) : this.tickets;
    },
  },
  watch: {
    ticketDetails: {
      deep: true,
      handler(details) {
        const count = details && details.ticketcomments ? details.ticketcomments.length : 0;
        if (count > this.lastMessageCount) this.$nextTick(this.scrollToBottom);
        this.lastMessageCount = count;
      },
    },
  },
  methods: {
    moment,
    ...mapActions("tickets", [TICKETS__ACTIONS.CREATE, TICKETS__ACTIONS.DETAILS, TICKETS__ACTIONS.REPLY, TICKETS__ACTIONS.TICKETS]),
    ticketIcon(subject = "") {
      const value = subject.toLowerCase();
      if (value.includes("deposit") || value.includes("withdraw")) return "mdi-cash-multiple";
      if (value.includes("account")) return "mdi-account-outline";
      return "mdi-ticket-confirmation-outline";
    },
    ticketIconClass(subject = "") {
      const value = subject.toLowerCase();
      if (value.includes("deposit") || value.includes("withdraw")) return "ticket-icon--green";
      if (value.includes("account")) return "ticket-icon--purple";
      return "ticket-icon--blue";
    },
    async selectTicket(ticket, index) {
      this.selectedTab = index;
      await this[TICKETS__ACTIONS.DETAILS](ticket.id);
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    scrollToBottom() {
      const el = this.$refs.messageCard;
      if (el) el.scrollTop = el.scrollHeight;
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshInterval = setInterval(async () => {
        await this[TICKETS__ACTIONS.TICKETS]();
        if (this.ticketDetails && this.ticketDetails.id) await this[TICKETS__ACTIONS.DETAILS](this.ticketDetails.id);
      }, 10000);
    },
    stopAutoRefresh() {
      if (this.refreshInterval) clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    },
    async createTicket() {
      if (!this.selectedOption) return toast.error("Please select a topic");
      if (this.selectedOption === "Others" && !this.subject.trim()) return toast.error("Please enter a subject");
      if (!this.description.trim()) return toast.error("Please write a description");
      await this[TICKETS__ACTIONS.CREATE]({ subject: this.selectedOption === "Others" ? this.subject : this.selectedOption, description: this.description });
      this.dialog = false; this.selectedOption = null; this.subject = ""; this.description = "";
      await this[TICKETS__ACTIONS.TICKETS]();
      if (this.tickets[0]) await this.selectTicket(this.tickets[0], 0);
    },
    handleImageUpload(file) {
      if (!file) return this.clearImage();
      this.selectedImage = file;
      const reader = new FileReader();
      reader.onload = event => { this.imagePreview = event.target.result; };
      reader.readAsDataURL(file);
    },
    clearImage() { this.selectedImage = null; this.imagePreview = null; },
    async submitReply(id) {
      if (!id || (!this.comment.trim() && !this.selectedImage)) return;
      try {
        this.isPendingReply = true;
        await this[TICKETS__ACTIONS.REPLY]({ id, comment: this.comment.trim(), file: this.selectedImage });
        this.comment = ""; this.clearImage();
        this.$nextTick(this.scrollToBottom);
      } finally { this.isPendingReply = false; }
    },
  },
  async mounted() {
    await this[TICKETS__ACTIONS.TICKETS]();
    if (this.tickets[0]) await this.selectTicket(this.tickets[0], 0);
    this.startAutoRefresh();
  },
  beforeDestroy() { this.stopAutoRefresh(); },
};
</script>

<style lang="scss" scoped>
.support-page { min-height: 100%; padding: 12px 14px; color: #171a1f; background: #f7f8fa; }
.support-page--dark { color: #f4f5f7; background: radial-gradient(circle at 72% 20%, rgba(255, 196, 0, .055), transparent 32%), #030b13; }
.support-container { max-width: 1680px; padding: 0 !important; }
.support-heading { min-height: 60px; display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; padding: 0 8px 12px; }
.support-heading h1 { margin: 0 0 3px; font-size: 24px; line-height: 1.2; letter-spacing: -.4px; }
.support-heading p { margin: 0; color: #626973; font-size: 14px; }
.support-page--dark .support-heading p { color: #aeb6c0; }
.new-support-btn, .send-btn { height: 40px !important; padding: 0 17px !important; border-radius: 7px !important; color: #17130a !important; font-size: 13px; font-weight: 700; text-transform: none; background: linear-gradient(135deg, #ffd43b, #ffc400) !important; box-shadow: 0 7px 18px rgba(255, 196, 0, .13); }
.support-modal { border: 1px solid #5a470c; background: #07111b !important; color: #f4f5f7 !important; }
.support-modal::v-deep .support-form-field fieldset {
  border-width: 1px;
  border-color: #4b5148;
  transition: border-color .18s ease, box-shadow .18s ease;
}
.support-modal::v-deep .support-form-field .v-label { color: #aeb8cc; }
.support-modal::v-deep .support-form-field input,
.support-modal::v-deep .support-form-field textarea,
.support-modal::v-deep .support-form-field .v-select__selection,
.support-modal::v-deep .support-form-field .v-icon { color: #ffc400; }
.support-modal::v-deep .support-form-field.v-input--is-focused fieldset {
  border-width: 2px;
  border-color: #ffc400 !important;
  box-shadow: 0 0 0 3px rgba(255, 196, 0, .15);
}
.support-modal::v-deep .support-form-field.v-input--is-focused .v-label { color: #ffc400 !important; }
.support-modal::v-deep .support-form-field.v-input--is-focused .v-input__slot { background: rgba(255, 196, 0, .035); }
.support-workspace { height: calc(100vh - 140px); min-height: 440px; display: grid; grid-template-columns: 350px minmax(0, 1fr); overflow: hidden; border: 1px solid #293026; border-radius: 8px; background: #07111b; }
.ticket-panel { display: flex; min-width: 0; flex-direction: column; border-right: 1px solid #293026; background: linear-gradient(180deg, #0b1621, #07111b); }
.ticket-tools { display: flex; gap: 9px; padding: 10px; border-bottom: 1px solid #293026; }
.ticket-search { height: 38px; display: flex; flex: 1; align-items: center; gap: 9px; padding: 0 11px; border: 1px solid #343b32; border-radius: 7px; color: #aeb6c0; }
.ticket-search .v-icon, .filter-btn .v-icon, .more-btn .v-icon { color: #ffc400 !important; }
.ticket-search input { min-width: 0; width: 100%; color: inherit; outline: none; }
.ticket-search input::placeholder { color: #8290ad; }
.filter-btn, .more-btn { width: 38px !important; height: 38px !important; border: 1px solid #343b32; border-radius: 7px; color: #ffc400 !important; }
.ticket-list { min-height: 0; flex: 1; overflow-x: hidden; overflow-y: scroll; overscroll-behavior-y: auto; }
.ticket-list, .messages { scrollbar-width: thin; scrollbar-color: #4b5148 transparent; }
.ticket-list::-webkit-scrollbar, .messages::-webkit-scrollbar { width: 6px; height: 6px; }
.ticket-list::-webkit-scrollbar-track, .messages::-webkit-scrollbar-track { background: transparent; }
.ticket-list::-webkit-scrollbar-thumb, .messages::-webkit-scrollbar-thumb { border-radius: 10px; background: #4b5148; }
.ticket-list::-webkit-scrollbar-thumb:hover, .messages::-webkit-scrollbar-thumb:hover { background: #656b61; }
.ticket-item { position: relative; width: 100%; min-height: 88px; display: flex; align-items: center; gap: 12px; padding: 10px 13px 10px 17px; color: #eef0f2; text-align: left; border: 0; border-bottom: 1px solid #293026; background: transparent; cursor: pointer; }
.ticket-item--active { background: linear-gradient(100deg, rgba(255, 196, 0, .16), rgba(255, 196, 0, .035)); }
.ticket-item--active::before { position: absolute; inset: 0 auto 0 0; width: 3px; content: ""; background: #ffc400; }
.ticket-icon { width: 46px; height: 46px; display: inline-flex; flex: 0 0 46px; align-items: center; justify-content: center; border-radius: 9px; }
.ticket-icon--green { color: #00e1ad; background: rgba(0, 184, 148, .12); border: 1px solid rgba(0, 225, 173, .1); }
.ticket-icon--purple, .ticket-icon--blue { color: #ffc400; background: rgba(255, 196, 0, .1); border: 1px solid rgba(255, 196, 0, .16); }
.ticket-icon .v-icon { color: inherit; }
.ticket-copy { min-width: 0; flex: 1; }
.ticket-title-row, .ticket-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.ticket-title-row strong { overflow: hidden; font-size: 15px; text-overflow: ellipsis; white-space: nowrap; }
.ticket-preview { display: block; overflow: hidden; margin: 5px 0 3px; color: #a3afc9; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.ticket-meta { color: #9ba8c2; font-size: 12px; }
.status-pill { padding: 4px 10px; color: #00daa1; font-size: 11px; font-weight: 600; border-radius: 12px; background: rgba(0, 201, 143, .12); }
.status-pill--closed { color: #ff668c; background: rgba(255, 67, 118, .1); }
.ticket-footer { padding: 11px 14px; color: #9ca9c4; font-size: 11px; }
.no-results { padding: 35px 20px; color: #8491ab; text-align: center; }
.conversation-panel { min-width: 0; display: grid; grid-template-rows: auto minmax(0, 1fr) auto; background: radial-gradient(circle at 65% 28%, rgba(255, 196, 0, .04), transparent 36%), #050e17; }
.conversation-header { min-height: 80px; display: flex; align-items: center; gap: 13px; padding: 11px 16px; border-bottom: 1px solid #293026; }
.conversation-title { min-width: 0; flex: 1; }
.conversation-title > div { display: flex; align-items: center; gap: 12px; }
.conversation-title h2 { margin: 0; font-size: 17px; }
.conversation-title p { display: flex; flex-wrap: wrap; gap: 14px; margin: 6px 0 0; color: #a4b0ca; font-size: 11px; }
.conversation-title p span { display: inline-flex; align-items: center; gap: 5px; }
.conversation-title .v-icon { color: #8391ad; }
.messages { min-height: 0; overflow-x: hidden; overflow-y: scroll; padding: 16px 18px; overscroll-behavior-y: auto; }
.message-row { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 14px; }
.message-row--user { justify-content: flex-end; }
.message-bubble { width: min(520px, 75%); padding: 13px 18px; border: 1px solid #343b32; border-radius: 8px; color: #eef0f2; background: linear-gradient(135deg, #101923, #0b151f); }
.message-bubble--user { border-color: #5a470c; background: linear-gradient(135deg, #1d1b11, #121710); }
.message-info { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; color: #9eabc6; font-size: 11px; }
.message-info span:last-child { margin-left: auto; }
.message-row--user .message-info { justify-content: flex-end; }
.message-row--user .message-info span { margin-left: 0; }
.message-info em { padding: 3px 10px; color: #ffc400; font-style: normal; border-radius: 5px; background: rgba(255, 196, 0, .11); }
.message-bubble p { margin: 0; line-height: 1.55; }
.staff-avatar, .user-avatar { position: relative; width: 36px; height: 36px; display: inline-flex; flex: 0 0 36px; align-items: center; justify-content: center; overflow: hidden; border: 2px solid #c99900; border-radius: 50%; }
.staff-avatar img { width: 100%; height: 100%; object-fit: cover; }
.staff-avatar i { position: absolute; right: 0; bottom: 1px; width: 10px; height: 10px; border: 2px solid #0b1424; border-radius: 50%; background: #23d37a; }
.user-avatar { border: 0; color: #17130a; background: linear-gradient(135deg, #ffd43b, #d99a00); }
.user-avatar .v-icon { color: #17130a !important; }
.reply-box { min-height: 0; margin: 0 12px 12px; padding: 0 12px 10px; overflow: hidden; border: 1px solid #293026; border-radius: 8px; background: linear-gradient(135deg, #0b1621, #07111b); }
.reply-tabs { height: 36px; display: flex; align-items: stretch; gap: 8px; border-bottom: 1px solid #343b32; }
.reply-tabs button { padding: 0 15px; color: #a5b2cd; border: 0; border-bottom: 2px solid transparent; background: transparent; }
.reply-tabs button.active { color: #ffc400; border-color: #ffc400; }
.reply-box textarea { box-sizing: border-box; width: 100%; height: 52px; resize: none; padding: 8px 2px; color: #e0e7f7; outline: 0; border: 0; background: transparent; }
.reply-box textarea::placeholder { color: #8390aa; }
.reply-actions { display: flex; align-items: center; justify-content: space-between; }
.reply-actions > div { display: flex; align-items: center; gap: 15px; color: #aebbd4; }
.reply-actions .v-icon { color: #ffc400; cursor: pointer; }
.attachment-input { width: 24px; margin: 0; padding: 0; }
.attachment-input::v-deep .v-input__prepend-outer { margin: 0; }
.attachment-input::v-deep .v-input__control { display: none; }
.image-preview { display: flex; align-items: flex-start; gap: 4px; padding-bottom: 10px; }
.closed-notice { margin: 14px; padding: 15px; color: #98a5be; text-align: center; border: 1px solid #202e45; border-radius: 7px; }
.closed-notice .v-icon { margin-right: 7px; color: inherit; }
.empty-support { display: grid; min-height: 420px; place-items: center; align-content: center; gap: 10px; color: #aeb6c0; border: 1px solid #293026; border-radius: 9px; background: #07111b; }
.empty-support .v-icon { color: #ffc400 !important; }
.empty-support h2, .empty-support p { margin: 0; }

/* Light theme */
.support-page:not(.support-page--dark) {
  color: #171a1f;
  background: #f7f8fa;
}
.support-page:not(.support-page--dark) .support-heading p { color: #626973; }
.support-page:not(.support-page--dark) .support-workspace {
  border-color: #dfe5f0;
  background: #fff;
  box-shadow: 0 2px 10px rgba(40, 54, 88, .025);
}
.support-page:not(.support-page--dark) .ticket-panel {
  border-color: #e1e6ef;
  background: #fff;
}
.support-page:not(.support-page--dark) .ticket-tools,
.support-page:not(.support-page--dark) .ticket-item,
.support-page:not(.support-page--dark) .conversation-header {
  border-color: #e1e6ef;
}
.support-page:not(.support-page--dark) .ticket-search,
.support-page:not(.support-page--dark) .filter-btn,
.support-page:not(.support-page--dark) .more-btn {
  color: #52617f !important;
  border-color: #dce3ee;
  background: #fff;
}
.support-page:not(.support-page--dark) .ticket-search input::placeholder { color: #687794; }
.support-page:not(.support-page--dark) .ticket-item { color: #111a32; }
.support-page:not(.support-page--dark) .ticket-item:hover { background: #fafbff; }
.support-page:not(.support-page--dark) .ticket-item--active { background: linear-gradient(100deg, rgba(255, 196, 0, .12), rgba(255, 247, 223, .55)); }
.support-page:not(.support-page--dark) .ticket-preview,
.support-page:not(.support-page--dark) .ticket-meta,
.support-page:not(.support-page--dark) .ticket-footer { color: #586784; }
.support-page:not(.support-page--dark) .ticket-list,
.support-page:not(.support-page--dark) .messages { scrollbar-color: #c4ccda transparent; }
.support-page:not(.support-page--dark) .ticket-list::-webkit-scrollbar-thumb,
.support-page:not(.support-page--dark) .messages::-webkit-scrollbar-thumb { background: #c4ccda; }
.support-page:not(.support-page--dark) .ticket-icon--green { color: #00ae82; background: #e9faf5; border-color: #dcf6ed; }
.support-page:not(.support-page--dark) .ticket-icon--purple,
.support-page:not(.support-page--dark) .ticket-icon--blue { color: #b47f00; background: #fff7df; border-color: #f4dfa0; }
.support-page:not(.support-page--dark) .conversation-panel { background: radial-gradient(circle at 65% 28%, rgba(217, 154, 0, .025), transparent 35%), #fff; }
.support-page:not(.support-page--dark) .conversation-title p { color: #5e6b87; }
.support-page:not(.support-page--dark) .conversation-title .v-icon { color: #60708e; }
.support-page:not(.support-page--dark) .message-bubble {
  color: #15203b;
  border-color: #dce3ed;
  background: #fff;
  box-shadow: 0 3px 10px rgba(39, 54, 87, .025);
}
.support-page:not(.support-page--dark) .message-bubble--user { border-color: #ead79c; background: linear-gradient(135deg, #fff9e8, #fffdf7); }
.support-page:not(.support-page--dark) .message-info { color: #65728d; }
.support-page:not(.support-page--dark) .message-info em { color: #9d7000; background: #fff3cc; }
.support-page:not(.support-page--dark) .staff-avatar i { border-color: #fff; }
.support-page:not(.support-page--dark) .reply-box {
  border-color: #dfe5ee;
  background: #fff;
  box-shadow: 0 2px 8px rgba(39, 54, 87, .025);
}
.support-page:not(.support-page--dark) .reply-tabs { border-color: #e2e7ef; }
.support-page:not(.support-page--dark) .reply-tabs button { color: #53617e; }
.support-page:not(.support-page--dark) .reply-tabs button.active { color: #a87500; border-color: #d99a00; }
.support-page:not(.support-page--dark) .reply-box textarea {
  margin: 10px 0 7px;
  padding: 11px 14px;
  color: #17213c;
  border: 1px solid #dfe5ee;
  border-radius: 7px;
  background: #fff;
}
.support-page:not(.support-page--dark) .reply-box textarea::placeholder { color: #76839e; }
.support-page:not(.support-page--dark) .reply-actions .v-icon { color: #5e6e8c; }
.support-page:not(.support-page--dark) .ticket-search .v-icon,
.support-page:not(.support-page--dark) .filter-btn .v-icon,
.support-page:not(.support-page--dark) .more-btn .v-icon,
.support-page:not(.support-page--dark) .reply-actions .v-icon,
.support-page:not(.support-page--dark) .empty-support .v-icon { color: #b47f00 !important; }
.support-page:not(.support-page--dark) .closed-notice {
  color: #65728d;
  border-color: #dfe5ee;
  background: #fafbfe;
}
.support-page:not(.support-page--dark) .empty-support {
  color: #687691;
  border-color: #dfe5ee;
  background: #fff;
}
.support-modal--light {
  color: #131d36 !important;
  border-color: #dfe5ee;
  background: #fff !important;
}
.support-modal--light::v-deep .support-form-field fieldset { border-color: #aeb9cb; }
.support-modal--light::v-deep .support-form-field .v-label { color: #65728a; }
.support-modal--light::v-deep .support-form-field input,
.support-modal--light::v-deep .support-form-field textarea,
.support-modal--light::v-deep .support-form-field .v-select__selection,
.support-modal--light::v-deep .support-form-field .v-icon { color: #17213a; }
.support-modal--light::v-deep .support-form-field.v-input--is-focused fieldset { border-color: #d99a00 !important; }
.support-modal--light::v-deep .support-form-field.v-input--is-focused .v-label { color: #a87500 !important; }
.support-modal--light::v-deep .support-form-field.v-input--is-focused .v-input__slot { background: #fffdf7; }

@media (max-width: 1100px) {
  .support-workspace { grid-template-columns: 320px minmax(0, 1fr); }
  .conversation-title p { gap: 8px 14px; }
}
@media (max-width: 760px) {
  .support-page {
    height: auto;
    min-height: 100%;
    overflow: visible;
    padding: 16px 10px 20px;
  }
  .support-heading { align-items: center; }
  .support-heading p { display: none; }
  .support-heading h1 { font-size: 23px; }
  .new-support-btn { padding: 0 14px !important; }
  .support-workspace {
    height: calc(100vh - 160px);
    height: calc(100dvh - 160px);
    min-height: 500px;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
    overflow: hidden;
  }
  .ticket-panel {
    min-height: 0;
    max-height: 164px;
    overflow: hidden;
    border-right: 0;
    border-bottom: 1px solid #293026;
  }
  .ticket-tools { padding: 10px; }
  .ticket-list {
    min-height: 0;
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x proximity;
    scrollbar-width: thin;
    touch-action: pan-x;
    -webkit-overflow-scrolling: touch;
  }
  .ticket-item {
    width: 270px;
    min-width: 270px;
    min-height: 92px;
    padding: 12px 14px;
    border-right: 1px solid #293026;
    border-bottom: 0;
    scroll-snap-align: start;
  }
  .ticket-icon { width: 44px; height: 44px; flex-basis: 44px; }
  .ticket-footer { display: none; }
  .conversation-panel {
    width: 100%;
    height: auto;
    min-height: 0;
    overflow: hidden;
  }
  .conversation-header { min-height: 94px; padding: 12px 15px; }
  .conversation-header > .ticket-icon { display: none; }
  .conversation-title p span:last-child { display: none; }
  .messages {
    height: 100%;
    min-height: 100px;
    overflow-y: auto;
    padding: 20px 12px;
    overscroll-behavior-y: auto;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
  }
  .message-bubble { width: calc(100% - 52px); padding: 15px; }
  .reply-box {
    position: relative;
    z-index: 2;
    flex-shrink: 0;
    margin: 0 8px 8px;
    padding: 0 10px 10px;
  }
  .reply-tabs { height: 42px; }
  .reply-box textarea { height: 68px; }
  .reply-actions { gap: 8px; }
  .reply-actions > div { gap: 10px; }
  .send-btn { min-width: 132px !important; height: 42px !important; padding: 0 14px !important; }
}

@media (max-width: 380px) {
  .support-heading { gap: 10px; }
  .new-support-btn { font-size: 12px; }
  .ticket-item { width: 245px; min-width: 245px; }
  .conversation-title p span:nth-child(2) { display: none; }
  .message-bubble { width: calc(100% - 44px); }
  .staff-avatar, .user-avatar { width: 36px; height: 36px; flex-basis: 36px; }
  .reply-actions > div .v-icon:nth-of-type(2) { display: none; }
  .send-btn { min-width: 116px !important; padding: 0 10px !important; }
  .support-workspace {
    height: calc(100vh - 150px);
    height: calc(100dvh - 150px);
    min-height: 460px;
  }
  .ticket-panel { max-height: 154px; }
  .conversation-header { min-height: 84px; }
  .reply-tabs { height: 36px; }
  .reply-box textarea { height: 58px; margin-top: 8px; }
}
</style>
